import type { IUseCurrentLocationResult } from '~/modules/location/hook/use-current-location';
import type { AggregateRequest } from '../../hook/use-aggregation';
import type {
    FilterState,
    FilterStates,
    FilterStatesKey,
} from '../../type/filter-state';
import type {
    CategoryFilter,
    CategoryFilterState,
} from './FilterCategorySelector';
import FilterCategorySelector from './FilterCategorySelector';
import type { CheckboxFilter, CheckboxFilterState } from './FilterCheckbox';
import FilterCheckbox from './FilterCheckbox';
import type {
    CheckboxGroupFilter,
    CheckboxGroupFilterState,
} from './FilterCheckboxGroup';
import FilterCheckboxGroup from './FilterCheckboxGroup';
import type { LocationFilter, LocationFilterState } from './FilterLocation';
import FilterLocation from './FilterLocation';
import type {
    RadioGroupFilter,
    RadioGroupFilterState,
} from './FilterRadioGroup';
import FilterRadioGroup from './FilterRadioGroup';
import type {
    RangeSelectorFilter,
    RangeSelectorFilterState,
} from './FilterRangeSelector';
import FilterRangeSelector from './FilterRangeSelector';
import type {
    SearchListFilter,
    SearchListFilterState,
} from './FilterSearchList';
import FilterSearchList from './FilterSearchList';
import type { DetailedPlace } from '~/modules/location/type';
import type {
    AwardsFilter,
    AwardFilterState,
} from '@modules/search/components/awards-filter-tree/AwardsFilterTree';
import AwardsFilterTree from '@modules/search/components/awards-filter-tree/AwardsFilterTree';

export type FilterType =
    | CategoryFilter
    | CheckboxFilter
    | CheckboxGroupFilter
    | RangeSelectorFilter
    | SearchListFilter
    | RadioGroupFilter
    | LocationFilter
    | AwardsFilter;

interface FilterFactoryProps extends IUseCurrentLocationResult {
    filterStates: FilterStates;
    filter: FilterType;
    state: FilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<FilterState>,
        updateStates?: boolean,
        subFilterKey?: string
    ) => void;
    findPlace: (placeSearch?: string | undefined) => Promise<void> | undefined;
    places: DetailedPlace[];
    aggregate: (request: AggregateRequest) => void;
    hasMarketingView?: boolean;
}

const FilterFactory = ({
    filterStates,
    filter,
    state,
    updateFilter,
    findPlace,
    places,
    currentLocationDisabled,
    currentLocation,
    getCurrentLocation,
    loading,
    aggregate,
    hasMarketingView,
}: FilterFactoryProps) => {
    switch (filter.kind) {
        case 'CategorySelector':
            const categorySelectorState = state as CategoryFilterState;
            if (!categorySelectorState.category.children?.length) return null;
            return (
                <FilterCategorySelector
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filter={filter}
                    updateFilter={updateFilter}
                    state={categorySelectorState}
                    disabled={loading}
                />
            );
        case 'Checkbox':
            const checkboxState = state as CheckboxFilterState;
            if (
                checkboxState.disabled ||
                filter.hideInFilters ||
                (filter.adminOnly && !hasMarketingView)
            ) {
                return null;
            }
            return (
                <FilterCheckbox
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filter={filter}
                    updateFilter={updateFilter}
                    state={checkboxState}
                    disabled={loading}
                />
            );
        case 'CheckboxGroup':
            const checkboxGroupState = state as CheckboxGroupFilterState;
            if (!checkboxGroupState.items.length) return null;
            return (
                <FilterCheckboxGroup
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filter={filter}
                    updateFilter={updateFilter}
                    state={checkboxGroupState}
                    disabled={loading}
                />
            );
        case 'AwardsFilter':
            const awardsFilterState = state as AwardFilterState;
            if (!awardsFilterState.items.length) return null;
            return (
                <AwardsFilterTree
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filter={filter}
                    {...awardsFilterState}
                    updateFilter={updateFilter}
                    disabled={loading}
                />
            );
        case 'RangeSelector':
            const rangeSelectorState = state as RangeSelectorFilterState;
            if (
                rangeSelectorState.range.min === rangeSelectorState.range.max &&
                !rangeSelectorState.range.max
            )
                return null;
            return (
                <FilterRangeSelector
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filter={filter}
                    updateFilter={updateFilter}
                    state={rangeSelectorState}
                    disabled={loading}
                />
            );
        case 'SearchList':
            const searchListState = state as SearchListFilterState;
            return (
                <FilterSearchList
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filterStates={filterStates}
                    filter={filter}
                    updateFilter={updateFilter}
                    state={searchListState}
                    aggregate={aggregate}
                    disabled={loading}
                />
            );
        case 'RadioGroup':
            const radioGroupState = state as RadioGroupFilterState;
            if (!radioGroupState.items?.length) return null;
            return (
                <FilterRadioGroup
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filter={filter}
                    updateFilter={updateFilter}
                    state={radioGroupState}
                    disabled={loading}
                />
            );
        case 'Location':
            const locationState = state as LocationFilterState;
            return (
                <FilterLocation
                    key={`${filter.filterKey}${filter.subFilterKey}`}
                    filter={filter}
                    updateFilter={updateFilter}
                    state={locationState}
                    findPlace={findPlace}
                    places={places}
                    currentLocationDisabled={currentLocationDisabled}
                    currentLocation={currentLocation}
                    getCurrentLocation={getCurrentLocation}
                    loading={loading}
                />
            );
        default:
            throw new Error(`Unsupported filter ${filter}`);
    }
};

export default FilterFactory;
